import { Menu, MenuItem } from '@blueprintjs/core';
import * as go from 'gojs';
import * as React from 'react';

import { ProjectResourceType } from 'src/constants/project';
import { TEST_IDS } from 'src/constants/testIds';
import { LinkCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { useRemoveResource } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveResource';
import { useAddMappingWithSelection } from 'src/containers/ProjectDiagram/hooks/useAddMappingWithSelection';
import {
  MappingLink,
  ResourceNode,
} from 'src/containers/ProjectDiagram/types/diagram';
import { isLink } from 'src/containers/ProjectDiagram/utils/graphObject';
import { getMappingsForResource } from 'src/containers/ProjectDiagram/utils/resource/getMappingsForResource';
import { EntityDrawerType } from 'src/store/ui/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useNavigateMapping } from 'src/utils/hooks/useNavigateMapping';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ResourceContextMenuProps = {
  node: ResourceNode;
};

export const ResourceContextMenu: React.VFC<ResourceContextMenuProps> = ({
  node,
}) => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();

  const navigateMapping = useNavigateMapping();

  const { handleAddMappingWithSelection, isNewMappingEnabled } =
    useAddMappingWithSelection();
  const handleRemoveResource = useRemoveResource();
  const handleSelectFirstMapping = React.useCallback(() => {
    if (!node) {
      return;
    }
    navigateMapping(getMappingsForResource(node)[0]?.data?.value || null);
  }, [node, navigateMapping]);

  return (
    <Menu>
      <MenuItem
        data-testid={TEST_IDS.projectDiagram.contextMenu.edit}
        onClick={() => {
          const entityValue = node.data.value;
          dispatch(
            uiSlice.actions.changedEntityDrawer({
              projectId,
              drawer: {
                entityValue,
                type: EntityDrawerType.RESOURCE,
              },
            })
          );
        }}
        text={copy.components.projectDiagram.contextMenu.seeDetails}
      />
      <MenuItem
        data-testid={TEST_IDS.projectDiagram.contextMenu.mapping}
        disabled={
          !new go.Set(node.linksConnected).any((link) =>
            isLink<MappingLink>(link, LinkCategory.MAPPING)
          )
        }
        onClick={handleSelectFirstMapping}
        text={copy.components.projectDiagram.contextMenu.mappings}
      />
      {isNewMappingEnabled ? (
        <MenuItem
          data-testid={TEST_IDS.projectDiagram.contextMenu.newMapping}
          onClick={handleAddMappingWithSelection}
          text={copy.components.projectDiagram.contextMenu.newMapping}
        />
      ) : null}
      <MenuItem
        data-testid={TEST_IDS.projectDiagram.contextMenu.preview}
        onClick={() => {
          const nodeType = node.data.type;
          if (nodeType === ProjectResourceType.FILE_UPLOAD) {
            dispatch(
              uiSlice.actions.toggledFileUploadPreviewDialog({
                isOpen: true,
                resourceId: node.data.value,
              })
            );
          }
          if (nodeType === ProjectResourceType.VIRTUAL_GRAPH) {
            dispatch(
              uiSlice.actions.toggledVirtualGraphPreviewDialog({
                isOpen: true,
                resourceId: node.data.value,
              })
            );
          }
        }}
        text={copy.components.projectDiagram.contextMenu.preview}
      />
      <MenuItem
        data-testid={TEST_IDS.projectDiagram.contextMenu.delete}
        onClick={() => handleRemoveResource({ key: node.data.key })}
        text={copy.components.projectDiagram.contextMenu.deleteResource}
      />
    </Menu>
  );
};
